import React from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import WhatsAppFloat from "@/components/sections/WhatsAppFloat";
import { Button } from "@/components/ui/button";
import { Target, Compass, Gem, Users, Scale, ShieldCheck, MessageCircle } from "lucide-react";
import { useLeadDialog } from "@/components/sections/LeadDialog";

const values = [
  { icon: Scale, title: "Transparencia", desc: "Información clara sobre tasas, CAE y costo total del crédito. Sin letra chica." },
  { icon: ShieldCheck, title: "Responsabilidad", desc: "Promovemos el crédito responsable y el cumplimiento de la normativa chilena (CMF, SERNAC)." },
  { icon: Users, title: "Cercanía", desc: "Atendemos a cada cliente con lenguaje claro y asesoría personalizada." },
  { icon: Gem, title: "Calidad del servicio", desc: "Trabajamos solo con entidades financieras con buena reputación en el mercado." },
];

export default function Nosotros() {
  const { open } = useLeadDialog();
  const openWA = () => open("nosotros");
  return (
    <div className="min-h-screen bg-white" data-testid="nosotros-page">
      <Header onCtaClick={openWA} />
      <main>
        <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24 border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
              Nosotros
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Somos Bancoorp
              <span className="block text-blue-600">
                intermediario de crédito en Chile
              </span>
            </h1>
            <p className="mt-6 max-w-3xl text-lg text-slate-600 leading-relaxed">
              Nacimos con un objetivo simple: ayudar a las personas y empresas
              en Chile a acceder de forma transparente a opciones de crédito,
              comparando ofertas de distintas entidades financieras en un solo
              lugar.
            </p>
          </div>
        </section>

        <section className="py-20 lg:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8">
              <div className="h-11 w-11 rounded-xl bg-blue-600 text-white flex items-center justify-center mb-5">
                <Target className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Nuestra misión</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Facilitar el acceso a crédito en Chile con información clara y
                comparable, para que cada persona o empresa pueda tomar
                decisiones financieras informadas y responsables.
              </p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8">
              <div className="h-11 w-11 rounded-xl bg-teal-500 text-white flex items-center justify-center mb-5">
                <Compass className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Nuestra visión</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Ser el intermediario de crédito más confiable de Chile,
                reconocido por la transparencia, el cumplimiento normativo y la
                calidad del servicio que entregamos.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                Nuestros valores
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Principios que guían nuestro trabajo
              </h2>
            </div>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white border border-slate-200 p-6"
                  data-testid={`value-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center mb-4">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Información corporativa
            </h2>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <dl className="divide-y divide-slate-200 text-sm">
                <div className="grid sm:grid-cols-3 px-6 py-4 gap-2">
                  <dt className="text-slate-500">Razón social</dt>
                  <dd className="sm:col-span-2 font-semibold text-slate-900">Bancoorp SpA</dd>
                </div>
                <div className="grid sm:grid-cols-3 px-6 py-4 gap-2">
                  <dt className="text-slate-500">Actividad</dt>
                  <dd className="sm:col-span-2 text-slate-700">
                    Intermediario de crédito (no es banco ni institución
                    financiera regulada por la CMF).
                  </dd>
                </div>
                <div className="grid sm:grid-cols-3 px-6 py-4 gap-2">
                  <dt className="text-slate-500">Dirección</dt>
                  <dd className="sm:col-span-2 text-slate-700">
                    Av. Apoquindo 1234, Piso 8, Oficina 802, Las Condes,
                    Santiago, Chile
                  </dd>
                </div>
                <div className="grid sm:grid-cols-3 px-6 py-4 gap-2">
                  <dt className="text-slate-500">Contacto</dt>
                  <dd className="sm:col-span-2 text-slate-700">
                    WhatsApp: contacta a nuestro asesor · Email:{" "}
                    <a href="mailto:contacto@bancoorp.cl" className="text-blue-600 hover:underline">
                      contacto@bancoorp.cl
                    </a>
                  </dd>
                </div>
                <div className="grid sm:grid-cols-3 px-6 py-4 gap-2">
                  <dt className="text-slate-500">Cumplimiento</dt>
                  <dd className="sm:col-span-2 text-slate-700">
                    Ley N° 19.628 (protección de datos), Ley N° 18.010
                    (operaciones de crédito), Ley del Consumidor (SERNAC) y
                    buenas prácticas alineadas con la CMF.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold">¿Tienes dudas sobre nosotros?</h2>
            <p className="mt-4 text-slate-300">
              Conversemos por WhatsApp. Estamos para ayudarte.
            </p>
            <Button
              onClick={openWA}
              className="mt-8 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full px-7 py-6 text-base font-semibold"
              data-testid="nosotros-cta"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Escribir por WhatsApp
            </Button>
          </div>
        </section>
      </main>
      <Footer onCtaClick={openWA} />
      <WhatsAppFloat />
    </div>
  );
}
